import esriRequest from '@arcgis/core/request.js';
import type { LayerProbe } from './types';

const STRING_DISTINCT_CAP = 50;
const HEAVY_TYPES = new Set([
  'esriFieldTypeBlob',
  'esriFieldTypeRaster',
  'esriFieldTypeXML',
  'esriFieldTypeGeometry',
  'esriFieldTypeGlobalID',
  'esriFieldTypeGUID',
]);
const STRING_TYPES = new Set([
  'esriFieldTypeString',
]);
const NUMERIC_TYPES = new Set([
  'esriFieldTypeSmallInteger',
  'esriFieldTypeInteger',
  'esriFieldTypeBigInteger',
  'esriFieldTypeSingle',
  'esriFieldTypeDouble',
]);

type EsriFieldDomain = {
  type: string;
  codedValues?: Array<{ code: string | number; name: string }>;
};

type EsriField = {
  name: string;
  type: string;
  alias?: string;
  nullable?: boolean;
  domain?: EsriFieldDomain | null;
};

type EsriServiceMeta = {
  geometryType?: string;
  objectIdField?: string;
  displayField?: string;
  maxRecordCount?: number;
  fields?: EsriField[];
  editingInfo?: { lastEditDate?: number };
};

type FetchJsonOptions = { token?: string };

async function fetchJson<T>(url: string, query: Record<string, string> = {}, opts: FetchJsonOptions = {}): Promise<T> {
  const res = await esriRequest(url, {
    query: { f: 'json', ...query, ...(opts.token ? { token: opts.token } : {}) },
    responseType: 'json',
  });
  return res.data as T;
}

async function probeStringDistinct(
  layerUrl: string,
  field: string,
): Promise<Array<string | number> | undefined> {
  try {
    const stats = JSON.stringify([
      {
        statisticType: 'count',
        onStatisticField: field,
        outStatisticFieldName: '_n',
      },
    ]);
    const res = await fetchJson<{
      features?: Array<{ attributes: Record<string, unknown> }>;
    }>(`${layerUrl}/query`, {
      where: '1=1',
      outStatistics: stats,
      groupByFieldsForStatistics: field,
      orderByFields: '_n DESC',
      resultRecordCount: String(STRING_DISTINCT_CAP + 1),
    });
    const feats = res.features ?? [];
    if (feats.length === 0 || feats.length > STRING_DISTINCT_CAP) return undefined;
    return feats
      .map((f) => f.attributes[field])
      .filter((v): v is string | number => v !== null && v !== undefined && (typeof v === 'string' || typeof v === 'number'));
  } catch {
    return undefined;
  }
}

async function probeNumericRange(
  layerUrl: string,
  field: string,
): Promise<{ min?: number; max?: number; dead?: boolean }> {
  try {
    const stats = JSON.stringify([
      { statisticType: 'min', onStatisticField: field, outStatisticFieldName: '_min' },
      { statisticType: 'max', onStatisticField: field, outStatisticFieldName: '_max' },
    ]);
    const res = await fetchJson<{
      features?: Array<{ attributes: { _min?: number; _max?: number } }>;
    }>(`${layerUrl}/query`, { where: '1=1', outStatistics: stats });
    const a = res.features?.[0]?.attributes;
    if (!a) return {};
    const min = typeof a._min === 'number' ? a._min : undefined;
    const max = typeof a._max === 'number' ? a._max : undefined;
    const dead = min !== undefined && max !== undefined && min === max;
    return { min, max, dead };
  } catch {
    return {};
  }
}

async function gatherWithConcurrency<T>(
  tasks: Array<() => Promise<T>>,
  concurrency = 4,
): Promise<T[]> {
  const out: T[] = new Array(tasks.length);
  let i = 0;
  async function worker(): Promise<void> {
    while (true) {
      const idx = i++;
      if (idx >= tasks.length) return;
      out[idx] = await tasks[idx]();
    }
  }
  const workers: Promise<void>[] = [];
  for (let k = 0; k < Math.min(concurrency, tasks.length); k++) workers.push(worker());
  await Promise.all(workers);
  return out;
}

export async function probeLayer(layerUrl: string): Promise<LayerProbe> {
  const meta = await fetchJson<EsriServiceMeta>(layerUrl);
  const fields = meta.fields ?? [];

  const fieldTasks = fields.map((field) => async () => {
    const base = {
      name: field.name,
      type: field.type,
      nullable: field.nullable,
      alias: field.alias && field.alias !== field.name ? field.alias : undefined,
    };
    if (HEAVY_TYPES.has(field.type)) return base;

    if (field.domain && field.domain.type === 'codedValue' && field.domain.codedValues) {
      const coded: Record<string, string> = {};
      for (const cv of field.domain.codedValues) {
        coded[String(cv.code)] = cv.name;
      }
      return { ...base, coded_values: coded };
    }

    if (STRING_TYPES.has(field.type)) {
      const distinct = await probeStringDistinct(layerUrl, field.name);
      return { ...base, distinct_sample: distinct };
    }

    if (NUMERIC_TYPES.has(field.type)) {
      const range = await probeNumericRange(layerUrl, field.name);
      return { ...base, ...range };
    }

    return base;
  });

  const enriched = await gatherWithConcurrency(fieldTasks, 4);

  return {
    probed_at: Date.now(),
    service: {
      geometry_type: meta.geometryType,
      object_id_field: meta.objectIdField,
      display_field: meta.displayField,
      max_record_count: meta.maxRecordCount,
      last_edit_date: meta.editingInfo?.lastEditDate,
    },
    fields: enriched,
  };
}
